import React, { ComponentType, ReactNode, useContext } from 'react';
import { getPlatformStyles } from '@direct-frontend/platform-code';

import { ListBaseItem, ListBaseItemProps } from '../ListBaseItem/ListBaseItem';
import { cn } from '../../../lib/css/className/className';
import { ListContext } from '../utils';
import { ColorlessIconProps } from '../../../icons';
import type { HelpHandler } from '../../Help';
import { ListIconRender } from '../ListIconRender/ListIconRender';
import { HStack } from '../../Stack';
import { getSizeSkin } from '../../../lib/skin/getSizeSkin/getSizeSkin';
import { TextGenericWithSkin } from '../../TextGeneric/TextGenericWithSkin';
import { SkinColor } from '../../../lib/skin/getColorSkin/generated/types';
import { getColorSkin } from '../../../lib/skin/getColorSkin/getColorSkin';

getPlatformStyles({
    common: import('./ListSection.css'),
    desktop: import('./ListSection@desktop.css'),
    mobile: import('./ListSection@mobile.css'),
});

const cls = cn('ListSection');

const skinMap: Record<'gray' | 'black', SkinColor> = {
    black: 'gray-neutral',
    gray: 'gray-transparent',
};

export type ListSectionProps = Omit<ListBaseItemProps, 'children'> &
    HelpHandler & {
        /**
         * Список элементов в секции
         */
        children?: ReactNode;

        /**
         * Заголовок секции
         */
        title?: string;

        /**
         * Дополнительный текст к заголовку
         */
        caption?: string;

        /**
         * Цвет заголовка
         * @default gray
         */
        color?: 'gray' | 'black';

        /**
         * Иконка, которую нужно отрисовать слева от основного контента. При отрисовке передается нужный размер и цвет
         * @example <ListSection iconLeft={IconAttach}>content</ListSection>
         * @example <ListSection iconLeft={props => <IconErrorFilled {...props} />}>content</ListSection>
         */
        iconLeft?: ComponentType<ColorlessIconProps>;
    };

/**
 * Компонент для создания секции с набором элементов
 * Если в списке используется секция, то все остальные элементы должны быть также внутри какой-то секции
 */
export const ListSection = function ListSection(props: ListSectionProps) {
    const { title, caption, color = 'gray', iconLeft, children, help } = props;
    const { size } = useContext(ListContext);

    const sizeSkinClass = getSizeSkin(size);

    // Если в секцию и не передавали детей, она используется как хак, чтобы добавить разделитель
    // Такие использования есть в VirtualList/ListBox/Menu
    const isIntentionalyEmpty = !children;

    const skinClass = getColorSkin(skinMap[color], {});

    return (
        <div className={cls({ intentionalyEmpty: isIntentionalyEmpty }, [sizeSkinClass, skinClass])}>
            {title && (
                <ListBaseItem className={cls('title', { size, 'has-icon-left': Boolean(iconLeft) })}>
                    <HStack gap={'current-geometry'}>
                        {iconLeft && (
                            <ListIconRender icon={iconLeft} role={color === 'gray' ? 'supplementary' : 'main'} />
                        )}
                        <TextGenericWithSkin
                            color="current"
                            role={color === 'gray' ? 'supplementary' : 'main'}
                            textSize={'current-main + current-caption'}
                            skinSize={size}
                            bold={color === 'black'}
                            caption={caption}
                            help={help}
                        >
                            {title}
                        </TextGenericWithSkin>
                    </HStack>
                </ListBaseItem>
            )}
            <ul className={cls('list')}>{children}</ul>
        </div>
    );
};
